import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { animationFrameScheduler, BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { Dictionary } from '@ngrx/entity';
// import { trackByUniqueName } from '@shared/utils';

import { ConversationsCollectionService, UsersCollectionService } from '../../../store/store-services';
import { ConversationStateModel } from '../../../store/store-models';
import { AppStateService } from '../../../store/app-state.service';
import { MessengerService } from '../messenger.service';
import { delay, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { UserDataModel } from '@modules/auth';
import { LayoutManageService } from 'src/app/pages/_layout/services/layout-manage.service';
import { MobileDetectorService } from '@services/MobileDetectorService/mobile-detector.service';
import { HttpUserProfileService } from '@services/HttpServices/UserProfileHttpService/http-user-profile.service';

const ONLINE_USERS_TTL_MS = 1000 * 60;
@Component({
  selector: 'app-chat-list-page',
  templateUrl: './chat-list-page.component.html',
  styleUrls: ['./chat-list-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatListPageComponent implements OnInit, OnDestroy {
  currentUser$ = this.appStateService.currentUser$;
  conversations$: Observable<ConversationStateModel[]> = this.conversationsService.entities$;
  allUsersMap$ = this.usersService.entityMap$;
  conversationsLoading$ = new BehaviorSubject(true);
  
  // trackByFn = trackByUniqueName;

  public isMobileDevice: boolean;
  public openUsersListModal: EventEmitter<boolean> = new EventEmitter();
  public usersListModalName: Subject<string> = new Subject();

  public onlineUsers$: Observable<UserDataModel[]>;
  public onlineUsersButtonTitle$: Observable<string>;
  public onlinePingIntervalRef?: NodeJS.Timer;

  @ViewChild('newChatButton', {static: true}) newChatButton!: TemplateRef<HTMLElement>;
  @ViewChild('viewOnlineButton', {static: true}) viewOnlineButton!: TemplateRef<HTMLElement>;

  private readonly pingOnlineUsers$ = new BehaviorSubject<undefined>(undefined);

  private readonly destroy$ = new Subject<void>();

  constructor(
    public appStateService: AppStateService,
    public messengerService: MessengerService,
    public conversationsService: ConversationsCollectionService,
    public usersService: UsersCollectionService,
    public layoutManage: LayoutManageService,
    public mobileDetector: MobileDetectorService,
    public httpUserProfileService: HttpUserProfileService,
  ) {
    this.onlineUsers$ = this.pingOnlineUsers$.pipe(
      switchMap(() => this.httpUserProfileService.getAllUsers({
        OnlineOnly: 'true',
        PageSize: '1000'
      })),
      map((response) => response.data), 
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );

    this.onlineUsersButtonTitle$ = this.onlineUsers$.pipe(
      map((users) => {
        // if (users.length < 1) return 
        return `${users.length} Online`
      })
    );
  }

  ngOnInit(): void {
    this.layoutManage.switchToSubheader({
      title: 'Chats',
      hideArrow: true,
      customWidget: this.viewOnlineButton,
      actionButton: this.newChatButton,
    });

    this.conversations$.pipe(
      tap((entities: ConversationStateModel[]) => {
        if (entities.length) {
          this.conversationsLoading$.next(false);
        } else {
          this.fetchConversations();
        }
      }),
      shareReplay(),
      takeUntil(this.destroy$),
    ).subscribe();

    this.onlinePingIntervalRef = setInterval(() => this.pingOnlineUsers$.next(undefined), ONLINE_USERS_TTL_MS);
  }

  ngOnDestroy(): void {
    this.openUsersListModal.next(false);
    this.layoutManage.resetState();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.onlinePingIntervalRef) {
      clearInterval(this.onlinePingIntervalRef);
    }
  }

  public openUsersList(isNewChat?: boolean) {
    this.usersListModalName.next(isNewChat ? 'New Chat' : 'Online Members');
    this.openUsersListModal.next(true);
  }

  public deleteChat(userId: string) {
    this.messengerService.deleteConversation(userId);
  }

  getParticipantUser(allUsersMap: Dictionary<UserDataModel>, conversation: ConversationStateModel) {
    const participantUserId = this.messengerService.parserOne2OneConversationUniqueName(conversation.uniqueName);
    return (allUsersMap || {})[participantUserId];
  }

  private fetchConversations() {
    (this.messengerService.chatClient ? of(this.messengerService.chatClient) : this.messengerService.chatClientReady$).pipe(
      tap((chatClient) => {
        console.log('[chatClient ready]', chatClient);
        chatClient.getSubscribedConversations().then((conversationsPaginator) => {
          const {items: conversations, hasPrevPage, hasNextPage} = conversationsPaginator;
          conversations.forEach(conversation => {
            this.messengerService.addConversation(conversation);
          });
          console.warn('[conversations response]', conversationsPaginator);
        });
      }),
      delay(100, animationFrameScheduler),
      tap(() => this.conversationsLoading$.next(false)),
      takeUntil(this.destroy$),
    ).subscribe((res) => {
        console.log('[conversations response] success', res);
      },
      (error) => {
        console.warn('[conversations response] error', error);
      });
  }
}
